import { useState } from "react";
import type { SluggedId } from "Models";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { ClubhouseAdKitImageDto } from "entities/responses/adkitImage";
import type { PlacementCatalogEntry } from "../../placementCatalog";
import { pluralize } from "../../pluralize";
import { ImagePreview } from "../library/ImageCard";
import { imageDisplayName } from "../library/imageLibraryViewModel";
import { ImagePickerDialog } from "./ImagePickerDialog";
import {
  ALL_HOLES,
  addSponsorImage,
  giveEmptyHolesTo,
  sponsoredHoleCount,
  toggleSponsorHole,
  type PlacementSetup,
} from "./campaignDraft";

interface HoleSponsorsProps {
  placement: PlacementCatalogEntry;
  setup: PlacementSetup;
  imagesById: Map<string, ClubhouseAdKitImageDto>;
  onChange: (setup: PlacementSetup) => void;
}

/** "Ridgeline Brewing owns holes 1 and 2": one sponsor image per hole. */
export function HoleSponsors({
  placement,
  setup,
  imagesById,
  onChange,
}: HoleSponsorsProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState<SluggedId | null>(
    setup.sponsorImages[0] ?? null,
  );
  // The selected chip can only be one that's still on the placement.
  const brush =
    selected && setup.sponsorImages.includes(selected)
      ? selected
      : (setup.sponsorImages[0] ?? null);
  const nameOf = (id: SluggedId) => {
    const image = imagesById.get(String(id));
    return image ? imageDisplayName(image) : "Image not found";
  };
  const sponsored = sponsoredHoleCount(setup);

  return (
    <>
      <p className="text-sm text-ink">
        <strong className="font-semibold">a.</strong> Add each sponsor&rsquo;s
        image, then select one.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {setup.sponsorImages.map((id) => {
          const image = imagesById.get(String(id));
          const on = id === brush;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={on}
              onClick={() => setSelected(id)}
              className={cn(
                "flex items-center gap-2 rounded-xl border-[1.5px] py-1.5 pr-3 pl-1.5 text-sm font-semibold text-ink transition-colors",
                on ? "border-primary" : "border-border hover:border-ink/40",
              )}
            >
              {image && <ImagePreview image={image} className="h-7 w-12" />}
              {nameOf(id)}
              <span className="font-medium text-text-muted">
                · {pluralize(sponsoredHoleCount(setup, id), "hole")}
              </span>
            </button>
          );
        })}
        <Button
          size="sm"
          variant="outline"
          className="rounded-lg text-xs"
          onClick={() => setPickerOpen(true)}
        >
          + Add a sponsor image
        </Button>
      </div>

      <p className="mt-1 text-sm text-ink">
        <strong className="font-semibold">b.</strong> Click the holes that
        sponsor owns. Click again to clear.{" "}
        {brush && (
          <button
            type="button"
            onClick={() => onChange(giveEmptyHolesTo(setup, brush))}
            className="font-semibold text-primary underline underline-offset-2"
          >
            Give all empty holes to {nameOf(brush)}
          </button>
        )}
      </p>

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6 lg:grid-cols-9">
        {ALL_HOLES.map((hole) => {
          const available = setup.holes.includes(hole);
          const owner = available ? setup.sponsorByHole[hole] : undefined;
          const ownerImage = owner ? imagesById.get(String(owner)) : undefined;
          return (
            <button
              key={hole}
              type="button"
              disabled={!available || !brush}
              aria-label={`Hole ${hole}: ${owner ? nameOf(owner) : available ? "no sponsor" : "not shown on this hole"}`}
              onClick={() =>
                brush && onChange(toggleSponsorHole(setup, hole, brush))
              }
              className={cn(
                "flex min-h-18 flex-col items-center justify-center gap-1 rounded-xl border-[1.5px] p-1.5 text-xs font-semibold text-ink transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                owner ? "border-primary" : "border-border hover:border-ink/40",
              )}
            >
              <span>Hole {hole}</span>
              {ownerImage ? (
                <ImagePreview image={ownerImage} className="h-6 w-12" />
              ) : (
                <span className="text-[11px] font-medium text-text-muted">
                  {available ? "No sponsor" : "Off"}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <p className="text-sm text-text-muted">
        {sponsored} of {setup.holes.length} holes have a sponsor. Empty holes
        will remain empty.
      </p>

      <ImagePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        placement={placement}
        addedIds={setup.sponsorImages.map(String)}
        onAdd={(image) => {
          onChange(addSponsorImage(setup, image.id));
          setSelected(image.id);
        }}
      />
    </>
  );
}
